// src/components/admin/SpecList.tsx

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import SpecDiff from '../common/SpecDiff';

interface Spec {
    key: string;
    value: string;
    action?: string;
    old_value?: string;
    _placeholder?: string;
}

interface SpecListProps {
    specs: Spec[];
    onChange: (specs: Spec[]) => void;
    recommendations?: Array<{ key: string; is_required?: boolean; category?: string; description?: string }>;
    disabled?: boolean;
    readOnly?: boolean;
    error?: any;
    label?: string;
    showRecommendations?: boolean;
    namePrefix?: string;
    showOverrideWarning?: boolean;
    parentSpecs?: Spec[];
    onClearError?: (index: number, field: 'key' | 'value') => void;
    isReviewMode?: boolean;
    changeSummary?: any;
    product?: any;
}

const SpecList: React.FC<SpecListProps> = ({
    specs = [],
    onChange,
    recommendations = [],
    disabled = false,
    readOnly = false,
    error,
    label = 'Specifications',
    showRecommendations = true,
    namePrefix = '',  // Default empty for product specs
    showOverrideWarning = false,
    parentSpecs = [],
    onClearError,
    isReviewMode = false,
    changeSummary,
    product,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [tooltipData, setTooltipData] = useState<{ key: string; description: string; x: number; y: number } | null>(null);

    const specErrors = error || {};
    const [showTooltip, setShowTooltip] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check if a spec key exists in parent specs
    const isOverridingParent = (key: string) => {
        if (!showOverrideWarning || !parentSpecs.length) return false;
        return parentSpecs.some(s => s.key.toLowerCase() === key.toLowerCase());
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleWheelEvent = (e: WheelEvent) => {
            e.preventDefault();
            container.scrollLeft += e.deltaY;
        };

        container.addEventListener('wheel', handleWheelEvent, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheelEvent);
        };
    }, [recommendations]);

    const handleSpecChange = (index: number, field: 'key' | 'value', value: string, fieldName: string) => {
        const updated = [...specs];
        updated[index] = { ...updated[index], [field]: value };
        onChange(updated);

        // Clear error for this specific field when typing
        if (specErrors[index]?.[field]) {
            const newErrors = { ...specErrors[index] };
            delete newErrors[field];
            console.log(fieldName)
            if (onClearError) {
                onClearError(index, field);
            }
        }
    };

    const addSpec = () => {
        onChange([...specs, { key: '', value: '' }]);
    };

    const removeSpec = (index: number) => {
        onChange(specs.filter((_, i) => i !== index));
    };

    const addRecommendation = (key: string, description?: string) => {
        if (specs.some(s => s.key.toLowerCase() === key.toLowerCase())) return;
        // Add the key with an empty value, but store the description as a placeholder hint
        onChange([...specs, { key, value: '', _placeholder: description ?? null }]);
    };

    // Handle drag scroll
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const container = e.currentTarget;
        let startX = e.pageX - container.offsetLeft;
        let scrollLeft = container.scrollLeft;

        const onMouseMove = (ev: MouseEvent) => {
            const x = ev.pageX - container.offsetLeft;
            const walk = (x - startX) * 1.5;
            container.scrollLeft = scrollLeft - walk;
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Build full specs with changes for review mode
    const buildFullSpecsWithChanges = () => {
        if (!isReviewMode || !changeSummary?.specs) {
            return specs;
        }

        const currentSpecs = specs || [];
        const specChanges = changeSummary.specs.changes || [];

        // Start with all current specs as unchanged
        const fullSpecs = currentSpecs.map(spec => ({
            key: spec.key,
            new_value: spec.value,
            action: 'unchanged'
        }));

        // Apply changes from change summary
        specChanges.forEach(change => {
            const index = fullSpecs.findIndex(s => s.key === change.key);
            if (change.action === 'removed') {
                if (index !== -1) {
                    fullSpecs[index].action = 'removed';
                    fullSpecs[index].old_value = change.old_value;
                    fullSpecs[index].new_value = change.old_value;
                }
            } else if (change.action === 'added' || change.action === 'changed') {
                if (index !== -1) {
                    fullSpecs[index].action = change.action;
                    fullSpecs[index].new_value = change.new_value;
                    fullSpecs[index].old_value = change.old_value;
                } else {
                    fullSpecs.push({
                        key: change.key,
                        new_value: change.new_value,
                        action: change.action,
                        old_value: change.old_value
                    });
                }
            }
        });

        return fullSpecs;
    };

    // Get unused recommendations (not in specs)
    const getUnusedRecommendations = () => {
        const specKeys = specs.map(s => s.key.toLowerCase());
        return recommendations.filter(
            r => !specKeys.includes(r.key.toLowerCase())
        );
    };

    // If in review mode, render SpecDiff + unused recommendations
    if (isReviewMode && changeSummary?.specs) {
        const fullSpecs = buildFullSpecsWithChanges();
        const unusedRecommendations = getUnusedRecommendations();

        return (
            <div className="space-y-4">
                {/* SpecDiff */}
                <SpecDiff
                    specChanges={fullSpecs}
                    summary={changeSummary.specs.summary}
                    label={label}
                />

                {/* Unused Recommendations */}
                {namePrefix === '' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">
                            Unused Recommendations:
                        </p>
                        {unusedRecommendations.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {unusedRecommendations.map((rec, index) => (
                                    <span
                                        key={index}
                                        className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500"
                                    >
                                        {rec.key}
                                        {rec.is_required && ' *'}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-green-600">
                                ✓ All recommended specs have been added
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2" data-field={namePrefix ? `${namePrefix}-specs` : 'specs'}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}{namePrefix && <span className="text-xs text-gray-400 ml-1">(shown alongside general specs)</span>}
            </label>

            {/* Recommended specs */}
            {showRecommendations && recommendations.length > 0 && !readOnly && (
                <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">
                        Recommended specs (click to add):
                    </p>
                    <div
                        ref={scrollContainerRef}
                        className="overflow-x-auto scrollbar-thin pb-1 cursor-grab active:cursor-grabbing"
                        onMouseDown={handleMouseDown}
                        style={{
                            scrollBehavior: 'smooth',
                            scrollbarWidth: 'thin',
                            WebkitOverflowScrolling: 'touch',
                            overflowY: 'hidden'
                        }}
                    >
                        <div className="flex gap-1.5 flex-nowrap min-w-max">
                            {recommendations.map((recommendation, index) => {
                                const alreadyExists = specs.some(
                                    s => s.key.toLowerCase() === recommendation.key.toLowerCase()
                                );
                                return (
                                    <div key={index} className="relative inline-block">
                                        <button
                                            type="button"
                                            onMouseEnter={(e) => {
                                                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltipData({
                                                    key: recommendation.key,
                                                    description: recommendation.description,
                                                    x: rect.left + rect.width / 2,
                                                    y: rect.top - 10
                                                });
                                                timeoutRef.current = setTimeout(() => {
                                                    setShowTooltip(true);
                                                }, 300);
                                            }}

                                            onMouseLeave={() => {
                                                if (timeoutRef.current) {
                                                    clearTimeout(timeoutRef.current);
                                                    timeoutRef.current = null;
                                                }
                                                setShowTooltip(false);
                                            }}
                                            className={`peer text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${alreadyExists
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                }`}
                                            onClick={() => addRecommendation(recommendation.key, recommendation.description)}
                                            disabled={alreadyExists}
                                        >
                                            {recommendation.key}
                                            {recommendation.is_required && ' *'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {recommendations.length > 8 && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                            ← Scroll for more recommendations →
                        </p>
                    )}
                </div>
            )}

            {/* Spec List */}
            {(!Array.isArray(specs) || specs.length === 0) ? (
                <div className="text-sm text-gray-400 italic">
                    No specifications added.
                </div>
            ) : (
                <div className="space-y-3">
                    {specs.map((spec, index) => {
                        const specError = error?.[index] || {};
                        const isOverriding = isOverridingParent(spec.key) && spec.key != '';

                        return (
                            <div key={index} className="space-y-1">
                                <div className="flex gap-2 items-start">
                                    {/* Key field */}
                                    <div className="w-1/3">
                                        <input
                                            type="text"
                                            name={`${namePrefix}spec-${index}-key`}
                                            value={spec.key}
                                            onChange={(e) => handleSpecChange(index, 'key', e.target.value, `${namePrefix}spec-${index}-key`)}
                                            placeholder="Key (e.g., Brand)"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${specError?.key ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                } ${isOverriding ? 'border-yellow-400 bg-yellow-50' : ''}`}
                                            disabled={disabled || readOnly}
                                            readOnly={readOnly}
                                        />
                                        {specError?.key && (
                                            <p className="mt-1 text-xs text-red-600">{specError.key}</p>
                                        )}
                                    </div>

                                    {/* Value field */}
                                    <div className="flex-1 relative group">
                                        <input
                                            type="text"
                                            name={`${namePrefix}spec-${index}-value`}
                                            value={spec.value}
                                            onChange={(e) => handleSpecChange(index, 'value', e.target.value, `${namePrefix}spec-${index}-key`)}
                                            placeholder="Enter a value"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${specError?.value ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                } ${isOverriding ? 'border-yellow-400 bg-yellow-50' : ''}`}
                                            disabled={disabled || readOnly}
                                            readOnly={readOnly}
                                        />
                                        {specError?.value && (
                                            <p className="mt-1 text-xs text-red-600">{specError.value}</p>
                                        )}
                                        {/* Tooltip - shows on hover */}
                                        {spec._placeholder && (
                                            <div className="absolute z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 bg-gray-300 text-gray-900 text-xs rounded p-2 max-w-xs break-words -top-2 left-0 transform -translate-y-full shadow-lg transition-all duration-200 delay-300">
                                                {spec._placeholder ?? "Enter a value"}
                                                {/* Arrow */}
                                                <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-300 transform rotate-45"></div>
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => removeSpec(index)}
                                            className="flex-shrink-0 mt-1 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove specification"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>

                                {/* Override warning */}
                                {isOverriding && (
                                    <p className="text-xs text-yellow-600 flex items-center gap-1.5 ml-1">
                                        <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
                                        This will override the product-level "{spec.key}" spec
                                    </p>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {!readOnly && (
                <button
                    type="button"
                    onClick={addSpec}
                    className="text-sm text-blue-600 hover:text-blue-700"
                >
                    + Add Specification
                </button>
            )}

            {typeof document !== 'undefined' && tooltipData && showTooltip && createPortal(
                <div
                    className="fixed z-50 bg-gray-300 text-gray-900 text-xs rounded p-2 max-w-xs break-words shadow-lg pointer-events-none transition-opacity duration-300"
                    style={{
                        left: tooltipData.x,
                        top: tooltipData.y,
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    {tooltipData.description}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-300 text-gray-900 transform rotate-45"></div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default SpecList;